/*
  Plan the download, so that we can figure out whether the download
  strategies support awaiting the completion of the downloading.
*/
import type { DownloadableItem, UnresolvedDownloadRequest } from '@mimir/client-common/fileSync/downloadPlanning';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import type { MediaSelection, TimeRange } from '@mimir/common/util/timeRange';
import { integerToFuzzy } from '@mimir/common/util/fuzzyMath';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import { isInElectron, isInPremiere } from '@mimir/common/context/applicationContext';
import { getMediaDuration } from '@mimir/common/util/item';


export interface SubclipInfo {
  timeRange: TimeRange;
  selectedSourceTimeRange: MediaSelection;
}

export interface UiDownloadConfig {
  isPartialHighresDownloadEnabled: boolean,
  isProxyOnlyOptimizationsEnabled: boolean,
  isProxyMode: boolean,
  isStandaloneMode: boolean,
  subclipInfo: SubclipInfo | undefined,
  remuxTsToMp4: boolean,
  wantGrowingArtifact: boolean,
  subclipExpansionRoom: number,
}

export function makeUiDownloadRequest(
  config: UiDownloadConfig,
  applicationContext: ApplicationContext,
  item: DownloadableItem,
  // If the item has an "aaf" property, the requested artefact
  // will be "aaf" regardless of what the "requestedArtefact" value is.
  // Otherwise, "requestedArtefact" is used if defined. If not defined,
  // we choose between "highres" or "proxy" dependending on whether we
  // are in highres or proxy mode
  requestedArtefact: ItemArtefact | undefined,
): UnresolvedDownloadRequest {
  let sourceMediaRange: MediaSelection;
  if (config.subclipInfo) {
    const selectedRange: MediaSelection = config.subclipInfo.selectedSourceTimeRange;
    sourceMediaRange = selectedRange;
  } else {
    const vci = item.virtualClipInfo;
    if (isInstanceOfVirtualClip(vci) && vci.inPoint != null && vci.outPoint != null) {
      sourceMediaRange = {
        whole: false,
        bounds: {
          start: { approximate: vci.inPoint / 1000 },
          end: { approximate: vci.outPoint / 1000 }
        }
      };
    } else {
      const mediaDuration = getMediaDuration(item);
      sourceMediaRange = {
        whole: true,
        bounds: {
          start: integerToFuzzy(0),
          end: { approximate: (mediaDuration ?? 0) / 1000 },
        }
      };
    }
  }
  const adobeOrElectron = isInPremiere(applicationContext) || isInElectron(applicationContext);
  const req: UnresolvedDownloadRequest = {
    item,
    timeRange: config.subclipInfo ? config.subclipInfo.timeRange : null,
    sourceMediaRange,
    isPremiereStandaloneMode: config.isStandaloneMode,
    isProxyWorkflowEnabled: config.isProxyMode,
    proxyOnlyOptimizations: adobeOrElectron && config.isProxyOnlyOptimizationsEnabled,
    partialHighresDownload: adobeOrElectron && config.isPartialHighresDownloadEnabled,
    requestedArtefact: resolveArtefact(item, requestedArtefact, config.isProxyMode),
    remuxTsToMp4: config.remuxTsToMp4,
    wantGrowingArtifact: config.wantGrowingArtifact,
    subclipExpansionRoom: config.subclipExpansionRoom,
  };
  return req;
}


function resolveArtefact(
  item: DownloadableItem,
  requestedArtefact: ItemArtefact | undefined,
  isProxyWorkflowEnabled: boolean,
): ItemArtefact {
  if (item.aaf) return 'aaf';
  if (requestedArtefact) return requestedArtefact;
  // Premiere only supports proxy workflows for video
  const isProxy = isProxyWorkflowEnabled && item.itemType === ItemType.VIDEO;

  return isProxy ? 'proxy' : 'highres';
}
